/**
 * The words a live's page says about time, as the confirmed prototype says them: the door's "Tonight, Tue Nov 3 ·
 * 6:00 PM ET" and "Starts in 3 min", the room's "Maya goes live at 6:00 PM", the day before's "Tomorrow, 6:00 PM",
 * and the next session's card. Pure, so the page and its tests agree. Days are the host's calendar's; the time a
 * guest reads as their own is their own clock's.
 */
import { daysUntil, labelParts } from "../../../shared/live-time";

/** "Tonight", "Today", "Tomorrow", or nothing once it is further off. */
function nearWord(atIso: string, tz: string, nowMs: number): string {
  const days = daysUntil(atIso, tz, nowMs);
  if (days === 0) {
    const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hour12: false }).format(new Date(atIso)));
    return hour >= 17 ? "Tonight" : "Today";
  }
  return days === 1 ? "Tomorrow" : "";
}

/** "Tonight, Tue Nov 3 · 6:00 PM ET": the door's line over the title. */
export function doorWhen(atIso: string, tz: string, nowMs: number): string {
  const p = labelParts(atIso, tz, "short");
  if (!p) return "";
  const day = `${p.weekday} ${p.month} ${p.day} · ${p.time} ${p.zone}`;
  const near = nearWord(atIso, tz, nowMs);
  return near ? `${near}, ${day}` : day;
}

/** "Starts in 3 min", "Starts in 1 hr 5 min", or "Starts any moment" once the time has come. */
export function startsInWords(msLeft: number): string {
  if (!Number.isFinite(msLeft) || msLeft <= 0) return "Starts any moment";
  const min = Math.max(1, Math.ceil(msLeft / 60_000));
  if (min < 60) return `Starts in ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `Starts in ${h} hr ${m} min` : `Starts in ${h} hr`;
}

/** "6:00 PM" on the guest's own clock. */
export function yourClock(atIso: string): string {
  return labelParts(atIso, undefined, "short")?.time ?? "";
}

/** "Maya goes live at 6:00 PM", the room's line over the countdown, on the guest's own clock. */
export function goesLiveWords(hostFirst: string, atIso: string): string {
  const time = yourClock(atIso);
  return time ? `${hostFirst} goes live at ${time}` : `${hostFirst} goes live soon`;
}

/** "Tomorrow, 6:00 PM", "Today, 6:00 PM", or "Tue Nov 3, 6:00 PM", on the guest's own clock. */
export function earlyDay(atIso: string, nowMs: number): string {
  const p = labelParts(atIso, undefined, "short");
  if (!p) return "";
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const near = nearWord(atIso, zone, nowMs);
  return near ? `${near === "Tonight" ? "Today" : near}, ${p.time}` : `${p.weekday} ${p.month} ${p.day}, ${p.time}`;
}

/** "Tue Nov 3 at 6:00 PM, your time. This page turns into the live when Maya starts." */
export function earlyNote(atIso: string, hostFirst: string): string {
  const p = labelParts(atIso, undefined, "short");
  const when = p ? `${p.weekday} ${p.month} ${p.day} at ${p.time}, your time. ` : "";
  return `${when}This page turns into the live when ${hostFirst} starts.`;
}

/** The next session's card: "TUE" over "10", "Next Tuesday · 6:00 PM ET", "Session 11: topic". */
export function nextCard(
  next: { startsAt: string; seq: number; topic: string | null },
  tz: string,
  nowMs: number,
): { weekday: string; day: string; when: string; title: string } {
  const short = labelParts(next.startsAt, tz, "short");
  const long = labelParts(next.startsAt, tz, "long");
  const days = daysUntil(next.startsAt, tz, nowMs);
  const day = days === 1 ? "Tomorrow" : days !== null && days > 1 && days < 8 ? `Next ${long?.weekday ?? ""}` : `${short?.month ?? ""} ${short?.day ?? ""}`;
  return {
    weekday: (short?.weekday ?? "").toUpperCase(),
    day: short?.day ?? "",
    when: short ? `${day} · ${short.time} ${short.zone}` : day,
    title: next.topic ? `Session ${next.seq}: ${next.topic}` : `Session ${next.seq}`,
  };
}

/** "Tue Nov 3": the day a session ran, on the host's calendar. */
export function sessionDay(atIso: string, tz: string): string {
  const p = labelParts(atIso, tz, "short");
  return p ? `${p.weekday} ${p.month} ${p.day}` : "";
}
